import React from 'react';
import { X } from 'lucide-react';
import { RobbieMark, Sheet } from '../../components/ui';
import WizardRobbieDocked, { WizardRobbieDockedProps } from './WizardRobbieDocked';

/**
 * Robbie on a phone: a button in the corner, and his whole panel as a sheet.
 *
 * On a desktop he is a permanent 230–300px rail, which is the right shape for
 * something you talk to while you work — both are on screen at once. A phone
 * has room for one of those two things, and the step content has to be it: an
 * RFQ is filled in, not chatted at. So Robbie waits in the corner, where he is
 * one tap away from anywhere in the wizard, and takes the lower two-thirds of
 * the screen when called — the reachable part, with the step still visible
 * above him so a question and its subject are never on different screens.
 *
 * The panel stays mounted while the sheet is shut. Robbie's conversation is
 * live — history, a reply in flight, a half-typed message, a recording — and
 * unmounting it on every dismissal would reload the thread each time somebody
 * glanced at him and cost them anything they had not sent yet.
 *
 * Open state belongs to the wizard, so a step that sends Robbie a message on
 * the buyer's behalf can open the sheet to show the answer arriving.
 */
export type WizardRobbieSheetProps = Omit<WizardRobbieDockedProps, 'className'> & {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const WizardRobbieSheet: React.FC<WizardRobbieSheetProps> = ({ open, setOpen, ...props }) => {

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ask Robbie"
          className="fixed bottom-s6 right-s6 z-40 flex h-[52px] w-[52px] items-center justify-center rounded-round border border-robbie-border bg-surface shadow-floating focus:outline-none focus-visible:ring-2 focus-visible:ring-robbie"
        >
          <RobbieMark size="sm" state="waving" glow aria-hidden />
        </button>
      )}

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        side="bottom"
        label="Robbie"
        keepMounted
        className="h-[72vh] bg-ai-panel"
      >
        <div className="relative flex h-full flex-col">
          {/* The grab bar a sheet is expected to have, and the close control
              beside it — the panel's own header below carries Robbie's state
              and its "new conversation" control, so this row stays a handle
              and nothing more. */}
          <div className="flex shrink-0 items-center px-s5 pt-s3">
            <span aria-hidden className="mx-auto h-[4px] w-[36px] rounded-round bg-line-strong" />
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close Robbie"
              className="absolute right-s3 top-s2 flex h-9 w-9 items-center justify-center rounded-control text-ink-muted hover:bg-surface hover:text-ink"
            >
              <X className="h-[17px] w-[17px]" />
            </button>
          </div>
          {/* The same panel the desktop rail renders — same chat, same chips,
              same mic — only its container changed. */}
          <WizardRobbieDocked {...props} className="min-h-0 flex-1 border-l-0" />
        </div>
      </Sheet>
    </>
  );
};

export default WizardRobbieSheet;
